import { post, get, patch } from "./divertidogsApiUtils";
import {
  objectToFormData,
  objectToFormDataOptionalValues,
} from "../utils/formDataUtils";
import { useQuery } from "@tanstack/react-query";
import { RespuestaArchivo } from "../utils/fileUtils";

export enum Sexo {
  Macho = "Macho",
  Hembra = "Hembra",
}

export enum Temperamento {
  Agresivo = "Agresivo",
  Temperamental = "Temperamental",
  Balanceado = "Balanceado",
  Sumiso = "Sumiso",
  Dominante = "Dominante",
  Amigable = "Amigable",
  Temeroso = "Temeroso",
}

export interface Raza {
  id: string;
  nombre: string;
}

export interface MascotaResumida {
  id: string;
  nombre: string;
  raza: string;
  edad: string;
}

export const leerRazas = async (): Promise<Array<Raza>> => {
  const resp = await get<Array<Raza>>("razas");
  if (resp) {
    return resp;
  }
  return [];
};

export interface NuevaMascota {
  nombre: string;
  idPropietario: string;
  idRaza: string;
  sexo: Sexo;
  fechaNacimiento: string;
  fechaIngreso: string;
  foto?: File;
  certificadoVacuna?: File;
  estaEsterilizada: Boolean;
  tieneCondicionMedica: Boolean;
  observacionesMedicas?: string;
  temperamento?: Temperamento;
  observacionesTemperamento?: string;
  observacionesAlimenticias?: string;
  nombreVeterinario?: string;
  comentarios?: string;
  fechaGarrapaticida?: string;
  mesesPeriodicidadGarrapaticida?: number;
  fechaVacuna?: string;
}

export const crearMascota = async (
  nuevaMascota: NuevaMascota
): Promise<any> => {
  const formData = objectToFormData(nuevaMascota);

  return post<FormData, any | undefined>("mascotas", formData);
};

export interface RespuestaBuscarMascotaParaMostrar {
  id: string;
  nombresPropietario: string;
  raza: Raza;
  nombre: string;
  sexo: Sexo;
  edad: string;
  fechaIngreso: string;
  tieneFoto: boolean;
  tieneCertificadoVacuna: boolean;
  estaEsterilizada: boolean;
  tieneCondicionMedica: boolean;
  observacionesMedicas: string;
  temperamento: Temperamento;
  observacionesTemperamento?: string;
  observacionesAlimenticias?: string;
  nombreVeterinario?: string;
  comentarios?: string;
  mesesPeriodicidadGarrapaticida?: string;
  mesesUltimoGarrapaticida?: string;
  mesesUltimaVacuna?: string;
}

export interface RespuestaBuscarMascota {
  id: string;
  nombresPropietario: string;
  raza: Raza;
  nombre: string;
  sexo: Sexo;
  fechaNacimiento: string;
  fechaIngreso: string;
  tieneFoto: boolean;
  tieneCertificadoVacuna: boolean;
  estaEsterilizada: boolean;
  tieneCondicionMedica: boolean;
  observacionesMedicas: string;
  temperamento: Temperamento;
  observacionesTemperamento?: string;
  observacionesAlimenticias?: string;
  nombreVeterinario?: string;
  comentarios?: string;
  mesesPeriodicidadGarrapaticida?: string;
  fechaGarrapaticida?: string;
  fechaVacuna?: string;
}

export const buscarMascotaParaMostrar = async (id: string) => {
  return get<RespuestaBuscarMascotaParaMostrar>(`mascotas/${id}/mostrar`);
};

export const buscarMascota = async (id: string) => {
  return get<RespuestaBuscarMascota>(`mascotas/${id}`);
};

const QUERY_BUSCAR_MASCOTA = "QUERY_BUSCAR_MASCOTA";

export const useBuscarMascota = (id: string) =>
  useQuery([QUERY_BUSCAR_MASCOTA], async () => await buscarMascota(id));

export const leerCertificadoVacuna = async (id: string) => {
  return get<RespuestaArchivo>(`mascotas/${id}/certificadoVacuna`);
};

export const QUERY_LEER_FOTO_MASCOTA = "QUERY_LEER_FOTO_MASCOTA";

export const useLeerFoto = (idMascota: string) =>
  useQuery([QUERY_LEER_FOTO_MASCOTA], async () => await leerFoto(idMascota));

export const leerFoto = async (id: string) => {
  return get<string>(`mascotas/${id}/foto`);
};

export interface ModificacionesMascota {
  nombre?: string;
  idPropietario?: string;
  idRaza?: string;
  sexo?: Sexo;
  fechaNacimiento?: string;
  fechaIngreso?: string;
  foto?: File;
  certificadoVacuna?: File;
  estaEsterilizada?: Boolean;
  tieneCondicionMedica?: Boolean;
  observacionesMedicas?: string;
  temperamento?: Temperamento;
  observacionesTemperamento?: string;
  observacionesAlimenticias?: string;
  nombreVeterinario?: string;
  comentarios?: string;
  fechaGarrapaticida?: string;
  mesesPeriodicidadGarrapaticida?: number;
  fechaVacuna?: string;
  eliminarFoto?: Boolean;
  eliminarCertificadoVacuna?: Boolean;
}

export const modificarMascota = async (
  id: string,
  modificaciones: ModificacionesMascota
) => {
  const formData = objectToFormDataOptionalValues(modificaciones);

  await patch<FormData, undefined>("mascotas/" + id, formData);
};

export interface ItemListadoMascotas {
  id: string;
  nombre: string;
  nombresPropietario: string;
  direccion?: string;
  celular: string;
  garrapaticidaAlDia: boolean;
  vacunaAlDia: boolean;
  raza: string;
}

export const listarMascotas = async (): Promise<
  ItemListadoMascotas[] | undefined
> => {
  return get<Array<ItemListadoMascotas>>("mascotas/listar");
};

export const QUERY_NUEVAS_MASCOTAS_DEL_MES = "QUERY_NUEVAS_MASCOTAS_DEL_MES";

export const useObtenerNuevasMascotasDelMes = () =>
  useQuery(
    [QUERY_NUEVAS_MASCOTAS_DEL_MES],
    async () => await obtenerNuevasMascotasDelMes()
  );

export const obtenerNuevasMascotasDelMes = async () => {
  return get<number>(`mascotas/nuevas_este_mes`);
};
